import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SavingsReviewWorksheet from './SavingsReviewWorksheet';
import OrderingRulesEditor from './OrderingRulesEditor';
import VendorBuyingModeModal from './VendorBuyingModeModal';
import { needsBuyingModeAnswer, setVendorFulfillment } from '../../services/vendorFulfillmentService';
import sharedSupabase from '../../config/supabase';
import {
  changedOrderLineIds,
  classifyPlanBillingBases,
  fetchOpenPlans,
  fetchParContextForPlan,
  fetchPlan,
  fetchReturnableDraftsForPlan,
  completeVendorAfterPreflight,
  confirmOptionPackageFacts,
  moveLineToOption,
  persistVisibleVendorSelections,
  returnPurchaseOrderToPlan,
  fetchShoppingCandidateGraphForStore,
  fetchWorkingSetGroupsForStore,
} from '../../services/purchasingPlanService';
import {
  fetchComparisonItems,
  fetchOptionsForItems,
  fetchLastPurchaseUseByOption,
} from '../../services/purchaseOptionService';
import { interpretPurchasePackage } from '../../services/purchaseUnitsService';
import { buildComparisonMap, splitLinesByItemLiveness } from '../../utils/purchasePlanning';
import { groupMembersFor } from '../../utils/relatedItemGroups';
import { fetchEquivalentGroupComparisons } from '../../services/itemRelationshipService';
import {
  shoppingCandidateItemIdsForItem,
} from '../../../../supabase/functions/_shared/shoppingCandidates.mjs';
import { requestShoppingComparisons } from '../../services/comparisonRecomputeService';
import { explainPurchaseScenario } from '../../services/purchaseScenarioExplanationService';

/**
 * SavingsReviewContainer
 *
 * Data shell around SavingsReviewWorksheet: loads the plan with its lines,
 * options, items and vendors; persists each drag as it lands (the worksheet
 * is a saved document, not a form with a submit button — closing the tab must
 * not lose an arrangement); and turns the worksheet's single Done action into
 * its vendor POs and shopping lists through the existing fan-out path.
 *
 * With no planId it renders the list of open plans for the store, which is
 * what the Ordering hub's Purchase Planner tab shows.
 */

const money = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const fmtDate = (value) => {
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

/**
 * Turn the raw PAR rows into per-item provenance the worksheet may show.
 * The gate is reconciliation: PAR − counted must reproduce the plan line's
 * frozen quantity_needed, or the provenance is withheld — a count edited
 * after plan creation must never contradict the need on screen.
 */
const composeParContext = (parRows, lines, optionsByItemId, vendorsById) => {
  const optionById = new Map();
  Object.values(optionsByItemId || {}).flat().forEach((option) => {
    if (option?.id != null) optionById.set(String(option.id), option);
  });
  const result = {};
  (lines || []).forEach((line) => {
    const itemId = String(line?.inventory_item_id ?? '');
    const entry = parRows?.[itemId];
    if (!entry) return;
    const need = Number(line.quantity_needed);
    const par = Number(entry.parLevel);
    const onHand = entry.countedOnHand === null || entry.countedOnHand === undefined
      ? null
      : Number(entry.countedOnHand);
    if (!Number.isFinite(par) || !Number.isFinite(need)) return;
    if (onHand !== null && Math.abs((par - onHand) - need) > 0.01) return;
    if (onHand === null && Math.abs(par - need) > 0.01) return;
    let parSource = null;
    const option = entry.parSourceOptionId ? optionById.get(String(entry.parSourceOptionId)) : null;
    if (option) {
      const vendorName = option.vendor_id != null
        ? vendorsById?.[option.vendor_id]?.display_name
        : option.vendor_name_text;
      const pack = option.pack_description || option.purchase_unit || null;
      if (vendorName) parSource = `set against ${vendorName}'s ${pack || 'package'}`;
      else if (pack) parSource = `set against the ${pack} package`;
    }
    result[itemId] = { parLevel: par, countedOnHand: onHand, parSource };
  });
  return result;
};

const SavingsReviewContainer = ({ storeId, planId = null, onOpenPlan, onViewOrders }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openPlans, setOpenPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [lines, setLines] = useState([]);
  // Lines whose inventory item was archived after this plan was created.
  // Hidden from the worksheet (and refused by the fan-out RPCs), but counted
  // here so the buyer is told rather than left hunting for a missing row.
  const [archivedLineCount, setArchivedLineCount] = useState(0);
  const [optionsByItemId, setOptionsByItemId] = useState({});
  const [itemsById, setItemsById] = useState({});
  // The store's confirmed EQUIVALENT groups: each line shops its whole group
  // (options pooled across siblings), exactly the pool that routed the plan
  // into review. Null = item-scoped, the old world, on any fetch failure.
  const [workingSetGroups, setWorkingSetGroups] = useState(null);
  // Shopping is deliberately broader than count identity: direct confirmed
  // Alternatives of the line's Equivalent closure, each expanded through its
  // own Equivalent closure. This graph never feeds count/catalog folding.
  const [shoppingCandidateGraph, setShoppingCandidateGraph] = useState(null);
  const [groupComparisonByKey, setGroupComparisonByKey] = useState({});
  const [shoppingComparisonByGroupKey, setShoppingComparisonByGroupKey] = useState({});
  const [vendorsById, setVendorsById] = useState({});
  // purchase_option_id -> ISO date this store last actually bought it (a sent
  // PO or a completed shopping trip). Backs the "Last used" badge, the same
  // fact Related Products already shows under that name.
  const [orderUseByOptionId, setOrderUseByOptionId] = useState({});
  // inventory_item_id → { parLevel, countedOnHand, parSource }: best-effort
  // PAR provenance from the source count, shown only when it reconciles with
  // the frozen quantity_needed. Enrichment — never blocks the worksheet.
  const [parContextByItemId, setParContextByItemId] = useState({});
  // Staleness token: a plan switch must not let plan A's slow PAR fetch land
  // its provenance on plan B's worksheet.
  const parContextRequestRef = useRef(0);
  // vendor id → the unsent draft PO that vendor's finished column produced.
  const [draftPoByVendorId, setDraftPoByVendorId] = useState({});
  const [busyVendorId, setBusyVendorId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [highlightedLineIds, setHighlightedLineIds] = useState([]);
  // The worksheet promises to translate supplier shorthand into a checkable
  // sentence before it asks the buyer to type package facts. Older options do
  // not always have the semantic reading stored yet, so resolve those rows on
  // demand and keep the proposal local until the buyer accepts it.
  const [packageAnalysisByOptionId, setPackageAnalysisByOptionId] = useState({});
  const packageAnalysisRequests = useRef(new Set());
  // The notice renders above the worksheet, but the button that produced it
  // (Finish, Confirm package) is often a full column-height away — an error
  // nobody sees reads as a hang. Bring the message to the buyer.
  const noticeRef = useRef(null);
  useEffect(() => {
    if (notice && noticeRef.current?.scrollIntoView) {
      noticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [notice]);
  useEffect(() => {
    if (!highlightedLineIds.length) return undefined;
    const timer = setTimeout(() => setHighlightedLineIds([]), 8000);
    return () => clearTimeout(timer);
  }, [highlightedLineIds]);
  // The first-order buying-mode question: { vendors, note, continueLabel,
  // resume } while it is up.
  const [modeQuestion, setModeQuestion] = useState(null);
  const answeredThisSession = useRef(new Set());

  const handleExplainScenario = useCallback(
    (scenario) => explainPurchaseScenario(plan?.store_id ?? storeId, scenario),
    [plan?.store_id, storeId],
  );
  // A finish is under way — from the press, THROUGH the buying-mode question,
  // to the last order. The worksheet freezes the arrangement while this is
  // set, because `hasPendingMoves` can only be checked before the press: the
  // question and the per-vendor round trips all happen with the sheet mounted
  // and clickable, and a switch started in that window is a selection the
  // database has not accepted being fanned out into a real order.
  const [finishInFlight, setFinishInFlight] = useState(false);
  // What the completion path reads. A finish runs across awaits and reloads,
  // and the copies captured when its callback was built go stale the moment
  // the first vendor's lines are stamped as fanned out.
  const planRef = useRef(plan);
  planRef.current = plan;
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const vendorsByIdRef = useRef(vendorsById);
  vendorsByIdRef.current = vendorsById;
  const itemsByIdRef = useRef(itemsById);
  itemsByIdRef.current = itemsById;

  useEffect(() => {
    packageAnalysisRequests.current.clear();
    setPackageAnalysisByOptionId({});
    // Invalidate any in-flight PAR fetch from the previous plan immediately.
    parContextRequestRef.current += 1;
    setParContextByItemId({});
  }, [planId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!planId) {
        setOpenPlans(await fetchOpenPlans(storeId));
        setPlan(null);
        setLines([]);
        // The refs are what the completion path treats as truth. Leaving them
        // on the plan we just navigated away from is how a finish could
        // preflight one plan's lines against another plan's id.
        planRef.current = null;
        linesRef.current = [];
        return { plan: null, lines: [] };
      }
      let loaded = await fetchPlan(planId);
      if (!loaded) throw new Error('This purchase plan no longer exists.');

      const lineItemIds = [...new Set(loaded.lines.map((l) => l.inventory_item_id).filter(Boolean))];
      // Load the one authoritative shopping graph, then widen only the data
      // fetch. The line remains attached to its Equivalent Count Item; the
      // additional ids are substitute products available for this review.
      const shoppingGraph = await fetchShoppingCandidateGraphForStore(loaded.store_id);
      const groups = shoppingGraph?.equivalentGroups
        || await fetchWorkingSetGroupsForStore(loaded.store_id);
      const itemIds = [...new Set(lineItemIds.flatMap((itemId) => {
        if (shoppingGraph) return shoppingCandidateItemIdsForItem(itemId, shoppingGraph).map(Number);
        const members = groupMembersFor(groups, itemId);
        return members ? [...members].map(Number) : [itemId];
      }))];
      let [options, items, vendorRows, drafts, groupComparisons] = await Promise.all([
        fetchOptionsForItems(loaded.store_id, itemIds),
        fetchComparisonItems(loaded.store_id, itemIds),
        sharedSupabase.from('vendors').select('*').eq('store_id', loaded.store_id),
        fetchReturnableDraftsForPlan(loaded.id),
        fetchEquivalentGroupComparisons(loaded.store_id),
      ]);

      // A decisively catch-weight package must be classified BEFORE this
      // review prices it, or the worksheet shows the pack-evidence estimate
      // while the fan-out recomputes packages × per-pound-rate — the review
      // that said $296 and the PO line that said $9.25. The write is guarded
      // (only a still-unclassified row at the same price), and the database
      // re-prices any open lines in the same transaction, so both the plan
      // and the options are re-read when anything was classified.
      const classified = await classifyPlanBillingBases(options || {}, items || {});
      if (classified > 0) {
        [loaded, options] = await Promise.all([
          fetchPlan(planId),
          fetchOptionsForItems(loaded.store_id, itemIds),
        ]);
        if (!loaded) throw new Error('This purchase plan no longer exists.');
      }
      const vendors = {};
      (vendorRows.data || []).forEach((v) => { vendors[v.id] = v; });

      // Archived items are not part of any order this review can create.
      const { live, archived } = splitLinesByItemLiveness(loaded.lines, items || {});

      setPlan(loaded);
      setLines(live);
      // The completion path reads these refs, and it reads them BETWEEN
      // renders — mid-run, after this reload, before React has painted. A ref
      // that only catches up on the next render is a stale arrangement, which
      // is what the finish preflight refuses.
      planRef.current = loaded;
      linesRef.current = live;
      setArchivedLineCount(archived.length);
      setOptionsByItemId(options || {});
      setItemsById(items || {});
      setWorkingSetGroups(groups);
      setShoppingCandidateGraph(shoppingGraph);
      setGroupComparisonByKey(groupComparisons || {});
      setShoppingComparisonByGroupKey({});
      setVendorsById(vendors);
      vendorsByIdRef.current = vendors;
      // Enrichment, deliberately after the plan is on screen: it degrades to
      // {} on any error and must never hold up the worksheet.
      try {
        fetchLastPurchaseUseByOption(
          loaded.store_id,
          Object.values(options || {}).flat().map((o) => o?.id).filter(Boolean),
        ).then(setOrderUseByOptionId).catch(() => {});
      } catch { /* an enrichment may never fail the plan */ }
      try {
        const parRequest = parContextRequestRef.current + 1;
        parContextRequestRef.current = parRequest;
        fetchParContextForPlan(loaded)
          .then((parRows) => {
            if (parContextRequestRef.current !== parRequest) return;
            setParContextByItemId(composeParContext(parRows, live, options || {}, vendors));
          })
          .catch(() => {
            if (parContextRequestRef.current !== parRequest) return;
            setParContextByItemId({});
          });
      } catch { /* PAR provenance is an explanation, never a requirement */ }
      setDraftPoByVendorId(drafts || {});
      return { plan: loaded, lines: live };
    } catch (err) {
      setError(err?.message || 'Failed to load the purchase plan.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [storeId, planId]);

  useEffect(() => { load(); }, [load]);

  /**
   * The post-finish re-read. NOT load().
   *
   * load() opens with setLoading(true), and the component early-returns a
   * "Loading the Purchase Planner…" screen while that is set — so every
   * Finish tore the whole worksheet down and rebuilt it, losing scroll, open
   * rows, filters and the notice, once per vendor in a bulk run. Nothing about
   * finishing warrants that: complete_allocation_vendor writes this plan's
   * row, its fanned-out lines, and the draft PO it just created. It cannot
   * touch options, items, vendors, the equivalent groups or the comparisons,
   * so re-reading those only costs round trips and re-runs
   * classifyPlanBillingBases' writes.
   *
   * Authoritative in exactly the way load() is: planRef and linesRef are
   * written BEFORE this returns, because the next vendor's preflight reads
   * them before React has painted. Returns null on failure so a run that can
   * no longer re-read its own plan stops instead of ordering blind.
   */
  const refreshPlanAfterFinish = useCallback(async () => {
    try {
      const loaded = await fetchPlan(planId);
      if (!loaded) throw new Error('This purchase plan no longer exists.');
      const drafts = await fetchReturnableDraftsForPlan(loaded.id);
      const { live, archived } = splitLinesByItemLiveness(loaded.lines, itemsByIdRef.current || {});
      setPlan(loaded);
      setLines(live);
      planRef.current = loaded;
      linesRef.current = live;
      setArchivedLineCount(archived.length);
      setDraftPoByVendorId(drafts || {});
      return { plan: loaded, lines: live };
    } catch (err) {
      setError(err?.message || 'Failed to reload the purchase plan.');
      return null;
    }
  }, [planId]);

  // AI chooses one meaningful denominator across the complete candidate set;
  // code uses its per-package quantities for arithmetic. Run after first paint
  // and fail soft: stored Equivalent comparisons remain usable, while an
  // incomparable substitute stays visible with a confirmation reason.
  const shoppingComparisonAnchorKey = useMemo(
    () => lines.map((line) => line.inventory_item_id).filter(Boolean).map(String).sort().join(','),
    [lines],
  );
  useEffect(() => {
    if (!plan?.store_id || !shoppingCandidateGraph || !shoppingComparisonAnchorKey) return undefined;
    let cancelled = false;
    requestShoppingComparisons(plan.store_id, shoppingComparisonAnchorKey.split(','))
      .then((result) => {
        if (cancelled || !result?.groups) return;
        const byKey = {};
        result.groups.forEach((group) => {
          if (group?.group_key != null && group?.ok) byKey[String(group.group_key)] = group;
        });
        setShoppingComparisonByGroupKey(byKey);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [plan?.store_id, shoppingCandidateGraph, shoppingComparisonAnchorKey]);

  const canonicalItemIdByGroupId = useMemo(() => Object.fromEntries(
    Object.entries(groupComparisonByKey || {}).map(([key, row]) => [key, row?.canonical_item_id]),
  ), [groupComparisonByKey]);

  const comparisonByItemId = useMemo(
    () => buildComparisonMap(itemsById, optionsByItemId, workingSetGroups, {
      shoppingCandidateGraph,
      groupComparisonByKey,
      shoppingComparisonByGroupKey,
      canonicalItemIdByGroupId,
    }),
    [
      itemsById, optionsByItemId, workingSetGroups, shoppingCandidateGraph,
      groupComparisonByKey, shoppingComparisonByGroupKey, canonicalItemIdByGroupId,
    ],
  );

  const optionById = useMemo(() => {
    const result = new Map();
    Object.values(optionsByItemId || {}).flat().forEach((option) => {
      if (option?.id != null) result.set(String(option.id), option);
    });
    return result;
  }, [optionsByItemId]);
  const optionByIdRef = useRef(optionById);
  optionByIdRef.current = optionById;

  // Vendors whose lines have all been fanned out are finished; the worksheet
  // locks their columns. Derived from the lines so a reload agrees with what
  // the RPC actually did.
  const completedVendorIds = useMemo(() => {
    const byVendor = new Map();
    lines.forEach((line) => {
      if (!line.selected_purchase_option_id) return;
      const option = optionById.get(String(line.selected_purchase_option_id));
      const vendorId = option?.vendor_id ?? line.vendor_id;
      if (vendorId == null) return;
      const key = String(vendorId);
      const entry = byVendor.get(key) || { total: 0, fanned: 0 };
      entry.total += 1;
      if (line.fanned_out_at) entry.fanned += 1;
      byVendor.set(key, entry);
    });
    return [...byVendor.entries()]
      .filter(([, entry]) => entry.total > 0 && entry.fanned === entry.total)
      .map(([key]) => key);
  }, [lines, optionById]);

  /**
   * A drag landed: persist it immediately and hand the RESULT back. The
   * worksheet shows the move as pending until this settles, so the returned
   * row (or the thrown error) is what decides where the card ends up.
   *
   * The failure path deliberately RETHROWS instead of swallowing the error and
   * reloading. Swallowing it is what produced the production mismatch: the
   * container reloaded the authoritative lines while the worksheet kept its
   * own override, so a refused move stayed on screen and was finished into a
   * shopping list that could never contain it. The worksheet needs the
   * rejection to roll its card back.
   */
  const handleLineMoved = useCallback(async (lineId, option) => {
    try {
      const updated = await moveLineToOption(lineId, option);
      setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...updated } : l)));
      return updated;
    } catch (err) {
      // Shown on the card by the worksheet; the banner carries it too, because
      // a card at the bottom of a tall column is easy to miss.
      setNotice({ kind: 'error', text: err?.message || 'Could not save that move.' });
      throw err;
    }
  }, []);

  // The back of a card: the buyer corrected what one package holds. Persist
  // as a human confirmation and reload so every line pricing off this option
  // recomputes from the corrected fact.
  const handleConfirmPackage = useCallback(async (optionId, facts) => {
    try {
      await confirmOptionPackageFacts(optionId, facts);
      setNotice({ kind: 'ok', text: 'Package contents updated — prices recalculated.' });
      await load();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.message || 'Could not save the package change.' });
    }
  }, [load]);

  // "Edit ordering rules" on an option row: open the ONE shared editor for
  // that purchase_options row. Saving updates the option; the DB's
  // order-facts trigger re-prices every open line selecting it, so the reload
  // is all the recalculation this side needs.
  const [orderingRulesOption, setOrderingRulesOption] = useState(null);
  const handleEditOrderingRules = useCallback((optionId) => {
    const option = optionById.get(String(optionId));
    if (!option) {
      setNotice({ kind: 'error', text: 'That vendor product could not be loaded. Reload and try again.' });
      return;
    }
    setOrderingRulesOption(option);
  }, [optionById]);
  const handleOrderingRulesSaved = useCallback(async () => {
    setNotice({ kind: 'ok', text: 'Ordering rules updated — order quantities recalculated.' });
    await load();
  }, [load]);

  // A legacy option can reach Savings Review with raw supplier wording but no
  // stored semantic interpretation. Reuse the package-analysis endpoint that
  // already powers the purchase-option editor. It parses first, asks the
  // model only when needed, and validates every returned number server-side.
  // A proposal stays browser-local; PackageRepairCard turns it into the
  // compact “We read that as…” confirmation before anything is written.
  const handleAnalyzePackage = useCallback(async (optionId) => {
    const key = String(optionId || '');
    if (!key || packageAnalysisRequests.current.has(key)) return;
    packageAnalysisRequests.current.add(key);
    setPackageAnalysisByOptionId((current) => ({
      ...current,
      [key]: { loading: true, attempted: false },
    }));

    try {
      const result = await interpretPurchasePackage({
        storeId: plan?.store_id ?? storeId,
        purchaseOptionId: key,
      });
      setPackageAnalysisByOptionId((current) => ({
        ...current,
        [key]: { ...result, loading: false, attempted: true },
      }));

      // A very strong AI reading is written by the endpoint itself. Reload so
      // the worksheet either prices the now-safe option or shows the server's
      // exact stored result rather than an older browser copy.
      if (result?.persisted) await load();
    } catch (analysisError) {
      setPackageAnalysisByOptionId((current) => ({
        ...current,
        [key]: {
          loading: false,
          attempted: true,
          interpretation: null,
          message: analysisError?.message || 'Package analysis is unavailable.',
        },
      }));
    }
  }, [load, plan?.store_id, storeId]);

  /**
   * Create ONE vendor's order. No buying-mode question and no busy guard —
   * both belong to the callers below, because a bulk finish asks its question
   * once for every vendor at the start, not once per vendor mid-run.
   *
   * Reads `linesRef`/`planRef` rather than the render's own copies. Finishing
   * vendor A stamps `fanned_out_at` on A's lines, which is an ORDER-AFFECTING
   * field: a bulk run holding the pre-finish array would fail vendor B's
   * preflight with "this plan changed" and send the buyer back to press Finish
   * by hand — the exact redundant click this screen is removing.
   *
   * Returns the sentence the caller should show, so a run over four vendors
   * says one thing at the end instead of overwriting its own notice four
   * times.
   */
  const runCompleteVendor = useCallback(async (vendorId, order) => {
    const vendorName = vendorsByIdRef.current[vendorId]?.display_name || 'Vendor';
    // The exact array handed to the preflight. The error path diffs against
    // THIS, never a fresh read of the ref: a render between the two reads
    // would otherwise compare the refusal to an arrangement nobody sent.
    let preflighted = linesRef.current;
    setBusyVendorId(String(vendorId));
    try {
      // A repaired package can become a visible recommendation after this
      // allocation line was created with no selected option. Finish is the
      // buyer's explicit acceptance of the arrangement on screen: persist any
      // such NULL selections first, then preflight and fan out that exact
      // persisted snapshot. This is why Sysco/Bev can no longer show an item
      // and subtotal, then answer "Nothing in this vendor's column".
      const prepared = await persistVisibleVendorSelections({
        order,
        persistedLines: linesRef.current,
        optionsById: optionByIdRef.current,
      });
      const linesForFinish = prepared.lines;
      preflighted = linesForFinish;
      if (prepared.persistedCount > 0) {
        setLines(linesForFinish);
        // The next vendor in a bulk run preflights against this, and it must
        // not wait for a render to catch up.
        linesRef.current = linesForFinish;
      }

      // THE ORDER BARRIER, server side of the browser's. Finish turns an
      // arrangement into money, and the fan-out reads PERSISTED selections —
      // so before creating anything, check that what this screen is showing is
      // what the database actually holds. A second tab, a colleague, or a move
      // that failed quietly would otherwise be discovered only in the PO.
      const { result } = await completeVendorAfterPreflight(
        planRef.current?.id,
        vendorId,
        linesForFinish,
        sharedSupabase,
      );
      const created = result.shoppingListId
        ? `${vendorName} shopping list created.`
        : `${vendorName} purchase order created.`;
      // THIS ORDER EXISTS whatever happens next. The re-read can still fail,
      // and when it does the refs keep describing the plan as it was BEFORE
      // this fan-out — so the run must stop rather than preflight the next
      // vendor against an arrangement the database has already moved past.
      const refreshed = await refreshPlanAfterFinish();
      if (!refreshed) {
        return {
          ok: true,
          fatal: true,
          vendorName,
          text: `${created} We could not reload the plan afterwards, so the remaining vendors were not finished — reload and finish them.`,
        };
      }
      return { ok: true, fatal: false, vendorName, text: created };
    } catch (err) {
      if (err?.code === 'savings_review_changed' || err?.code === 'allocation_revision_stale') {
        const refreshed = await load();
        const changed = err?.changedLineIds?.length
          ? err.changedLineIds
          : changedOrderLineIds(preflighted, refreshed?.lines || []);
        setHighlightedLineIds(changed);
        return {
          ok: false,
          // The plan itself moved, so every vendor after this one would be
          // refused for the same reason. Stop the run.
          fatal: true,
          vendorName,
          text: 'This purchase plan changed before the order was created. We refreshed it. Check the highlighted changes, then finish again.',
        };
      }
      return {
        ok: false,
        // Scoped to this vendor (inactive option, package needs review, empty
        // column). The other vendors are still finishable, so keep going.
        fatal: false,
        vendorName,
        text: err?.message || 'Could not create the order.',
      };
    } finally {
      setBusyVendorId(null);
    }
  }, [load, refreshPlanAfterFinish]);

  const handleCompleteVendor = useCallback(async (vendorId, order) => {
    if (busyVendorId) return;

    // Finishing a vendor is the moment "Order" stops being abstract: it either
    // emails a PO or builds a shopping list. If nobody has ever said which,
    // ask now — once — and resume the exact same action afterwards, so the
    // answer IS the finish. The DB column is the real gate; the ref only stops
    // a re-ask inside this session while `vendorsById` is still refetching.
    const vendor = vendorsById[vendorId];
    if (needsBuyingModeAnswer(vendor) && !answeredThisSession.current.has(String(vendorId))) {
      setFinishInFlight(true);
      setModeQuestion({
        vendors: [{ id: vendorId, name: vendor?.display_name || 'this vendor' }],
        note: 'Answering creates this order — there is nothing else to press.',
        resume: () => handleCompleteVendorRef.current(vendorId, order),
      });
      return;
    }

    setFinishInFlight(true);
    setNotice(null);
    try {
      const outcome = await runCompleteVendor(vendorId, order);
      setNotice({ kind: outcome.ok ? 'ok' : 'error', text: outcome.text });
    } finally {
      setFinishInFlight(false);
    }
  }, [busyVendorId, vendorsById, runCompleteVendor]);

  /**
   * Finish EVERY remaining vendor — the "Done → Create orders" path.
   *
   * The buying-mode question is asked once, up front, for all the vendors that
   * still owe an answer. That is what makes it the last interaction: the buyer
   * says how each vendor is bought from, and the orders exist. Asking inside
   * the loop instead let each vendor's question replace the one before it, so
   * exactly one order was created and every other vendor had to be finished
   * by hand from its card.
   */
  const handleCompleteVendors = useCallback(async (orders) => {
    if (busyVendorId) return { ok: false, busy: true };
    const seen = new Set();
    const queue = (orders || []).filter((order) => {
      const id = order?.vendorId == null ? null : String(order.vendorId);
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (!queue.length) return { ok: false, empty: true };

    const unanswered = queue
      .map((order) => String(order.vendorId))
      .filter((id) => (
        needsBuyingModeAnswer(vendorsByIdRef.current[id])
        && !answeredThisSession.current.has(id)
      ));
    if (unanswered.length) {
      setFinishInFlight(true);
      setModeQuestion({
        vendors: unanswered.map((id) => ({
          id,
          name: vendorsByIdRef.current[id]?.display_name || 'this vendor',
        })),
        note: queue.length === 1
          ? 'Answering creates this order — there is nothing else to press.'
          : `Answering creates all ${queue.length} orders — there is nothing else to press.`,
        continueLabel: `Create ${queue.length} order${queue.length === 1 ? '' : 's'}`,
        resume: () => handleCompleteVendorsRef.current(queue),
      });
      return { ok: false, deferred: true };
    }

    setFinishInFlight(true);
    setNotice(null);
    const outcomes = [];
    let stoppedAt = -1;
    try {
    for (let i = 0; i < queue.length; i += 1) {
      const order = queue[i];
      // eslint-disable-next-line no-await-in-loop
      const outcome = await runCompleteVendor(order.vendorId, order);
      outcomes.push(outcome);
      // Only a PLAN-level problem ends the run — the arrangement moved, or we
      // could not re-read it, so every vendor after this one would be refused
      // for the same reason. A vendor-scoped refusal (inactive option, package
      // needs review) says nothing about the others, and abandoning them would
      // put the buyer straight back into the per-vendor Finish hunt this whole
      // change exists to remove.
      if (outcome.fatal) { stoppedAt = i; break; }
    }

    const created = outcomes.filter((o) => o.ok);
    const failures = outcomes.filter((o) => !o.ok);
    const skipped = stoppedAt >= 0 ? queue.length - (stoppedAt + 1) : 0;

    const createdText = created.length === 0
      ? 'No orders were created.'
      : created.length === 1
        ? created[0].text
        : `${created.length} orders created for ${created.map((o) => o.vendorName).join(', ')}.`;

    if (!failures.length && !skipped) {
      // The reload-failed case is a successful order that still ends the run;
      // its own sentence already explains what to do.
      const halted = created.find((o) => o.fatal);
      setNotice({ kind: halted ? 'error' : 'ok', text: halted ? halted.text : createdText });
      if (!halted && onViewOrders) onViewOrders();
      return { ok: !halted, navigated: !halted && !!onViewOrders };
    }

    const parts = [createdText];
    failures.forEach((f) => parts.push(`${f.vendorName}: ${f.text}`));
    if (skipped > 0) {
      parts.push(`${skipped} vendor${skipped === 1 ? ' was' : 's were'} not finished — review the issue and press Done again.`);
    }
    setNotice({ kind: 'error', text: parts.join(' ') });
    return { ok: false, failures: failures.length, skipped };
    } finally {
      setFinishInFlight(false);
    }
  }, [busyVendorId, runCompleteVendor, onViewOrders]);

  /**
   * The undo of finishing a column: hand that vendor's unsent draft PO back to
   * the worksheet. The lines unfreeze, the column unlocks, and the buyer is
   * back where they were before they pressed Finish — no navigation, because
   * the whole point is to keep arranging this same sheet.
   */
  const handleReturnDraft = useCallback(async (vendorId) => {
    if (busyVendorId) return;
    const draft = draftPoByVendorId[String(vendorId)];
    if (!draft) return;
    setBusyVendorId(String(vendorId));
    setNotice(null);
    try {
      const result = await returnPurchaseOrderToPlan(draft.id);
      const vendorName = vendorsById[vendorId]?.display_name || 'That vendor';
      setNotice({
        kind: 'ok',
        text: `${vendorName}'s draft ${draft.po_number ? `(${draft.po_number}) ` : ''}was returned — ${result.returnedLineCount} item${result.returnedLineCount === 1 ? '' : 's'} back in the column.`,
      });
      await load();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.message || 'Could not return that draft.' });
      await load();
    } finally {
      setBusyVendorId(null);
    }
  }, [busyVendorId, draftPoByVendorId, vendorsById, load]);

  /**
   * "Sysco already has a draft PO — reopen it and move this here?" answered
   * yes. ONE operation from the buyer's point of view: hand the unsent draft
   * back, then land the move that wanted it. Order matters — the move cannot
   * be written while the line is still frozen by the draft.
   *
   * Any failure leaves the review readable: the draft either came back or it
   * did not, and the error says which. Nothing is created either way.
   */
  const handleReopenDraftAndMove = useCallback(async (vendorId, move) => {
    // draftPoByVendorId only ever holds UNSENT drafts (the query filters on
    // sent_at IS NULL) and return_po_to_savings_review refuses a sent PO
    // whatever its status says — so a sent order can never reach this path.
    const draft = draftPoByVendorId[String(vendorId)];
    if (!draft) throw new Error('That draft is no longer available.');
    setBusyVendorId(String(vendorId));
    setNotice(null);
    try {
      await returnPurchaseOrderToPlan(draft.id);
      // The line is unfrozen now, so the move can be persisted through the
      // ordinary path — same validation, same recalculation.
      const option = optionById.get(String(move.optionId));
      if (!option) throw new Error('That purchase option is no longer available.');
      await moveLineToOption(move.lineId, option);
      const vendorName = vendorsById[vendorId]?.display_name || 'That vendor';
      setNotice({
        kind: 'ok',
        text: `${vendorName}'s draft was reopened and the item moved — finish the column again when you are ready.`,
      });
    } finally {
      setBusyVendorId(null);
      // Reload either way: the draft may have come back even if the move did
      // not, and the worksheet must show what is actually there.
      await load();
    }
  }, [draftPoByVendorId, optionById, vendorsById, load]);

  // Both completion handlers resume themselves after the question is answered,
  // so each needs a stable handle on its own latest identity.
  const handleCompleteVendorRef = useRef(handleCompleteVendor);
  handleCompleteVendorRef.current = handleCompleteVendor;
  const handleCompleteVendorsRef = useRef(handleCompleteVendors);
  handleCompleteVendorsRef.current = handleCompleteVendors;

  const handleBuyingModeAnswer = useCallback(async (answers) => {
    // Every write must succeed before anything is created: an order placed
    // after a failed answer would ask again next time.
    await Promise.all(answers.map(([id, mode]) => setVendorFulfillment(id, mode)));
    const patched = {};
    answers.forEach(([id, mode]) => {
      answeredThisSession.current.add(String(id));
      const existing = vendorsById[id];
      if (existing) patched[id] = { ...existing, fulfillment_type: mode, fulfillment_type_source: 'user' };
    });
    if (Object.keys(patched).length) {
      setVendorsById((cur) => ({ ...cur, ...patched }));
      // resume() runs before the next render, and the completion path reads
      // the ref, not the state.
      vendorsByIdRef.current = { ...vendorsByIdRef.current, ...patched };
    }
    const resume = modeQuestion?.resume;
    setModeQuestion(null);
    if (resume) await resume();
  }, [vendorsById, modeQuestion]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#8b7b73', fontFamily: 'Montserrat, sans-serif' }}>Loading the Purchase Planner…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '1.1rem', fontFamily: 'Montserrat, sans-serif' }}>
        <div data-testid="savings-review-error" style={{ background: '#fdecea', border: '1px solid #f5c6c0', borderRadius: 8, color: '#a33b2e', fontSize: '0.85rem', padding: '0.6rem 0.75rem' }}>{error}</div>
      </div>
    );
  }

  // ── List mode: the hub tab ────────────────────────────────────────────────
  if (!plan) {
    return (
      <div style={{ padding: '1.1rem', fontFamily: 'Montserrat, sans-serif', color: '#3D2518' }}>
        <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Purchase Planner</h2>
        <p style={{ margin: '0.25rem 0 1rem', color: '#7a6e66', fontSize: '0.85rem' }}>
          Purchase plans from counts whose items are sold by more than one vendor.
          Arrange each one, then finish its vendors to create the purchase orders.
        </p>
        {openPlans.length === 0 ? (
          <div data-testid="savings-review-empty" style={{ color: '#8b7b73', fontSize: '0.85rem', padding: '1.25rem 0' }}>
            No purchase plans waiting. Completing a count with multi-vendor items creates one.
          </div>
        ) : (
          openPlans.map((row) => (
            <div
              key={row.id}
              data-testid="savings-review-list-row"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', border: '2px solid #EFE6DC', borderRadius: 12, background: '#fff', padding: '0.75rem 0.9rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}
            >
              <div>
                <strong style={{ fontSize: '0.92rem' }}>
                  Purchase plan{row.created_at ? ` from ${fmtDate(row.created_at)} count` : ''}
                </strong>
                <div style={{ color: '#7a6e66', fontSize: '0.8rem', marginTop: 2 }}>
                  {row.itemCount} item{row.itemCount === 1 ? '' : 's'} · Estimated {money(row.estimatedTotal)}
                  {row.unassignedCount > 0 ? ` · ${row.unassignedCount} without a vendor` : ''}
                </div>
                <div style={{ color: '#a35c2e', fontSize: '0.74rem', marginTop: 2, fontWeight: 600 }}>Needs vendor review</div>
              </div>
              <button
                type="button"
                data-testid="savings-review-open"
                onClick={() => onOpenPlan && onOpenPlan(row.id)}
                style={{ borderRadius: 999, padding: '0.55rem 1.1rem', border: '2px solid #5A3A28', background: '#3A2418', color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                Open Purchase Planner
              </button>
            </div>
          ))
        )}
      </div>
    );
  }

  // ── Worksheet mode ────────────────────────────────────────────────────────
  return (
    <div>
      {archivedLineCount > 0 && (
        <div
          data-testid="savings-review-archived-note"
          style={{
            margin: '0.75rem 1.1rem 0', borderRadius: 8, padding: '0.55rem 0.75rem', fontSize: '0.84rem',
            fontFamily: 'Montserrat, sans-serif', background: '#F4EFE9', border: '1px solid #E2D6C8', color: '#7a6e66',
          }}
        >
          {archivedLineCount} item{archivedLineCount === 1 ? ' was' : 's were'} archived after this
          count and {archivedLineCount === 1 ? 'is' : 'are'} not shown — archived items are never ordered.
        </div>
      )}
      {notice && (
        <div
          ref={noticeRef}
          data-testid="savings-review-notice"
          style={{
            margin: '0.75rem 1.1rem 0', borderRadius: 8, padding: '0.55rem 0.75rem', fontSize: '0.84rem',
            fontFamily: 'Montserrat, sans-serif',
            background: notice.kind === 'ok' ? '#EDF3EC' : '#fdecea',
            border: `1px solid ${notice.kind === 'ok' ? '#c9dcc6' : '#f5c6c0'}`,
            color: notice.kind === 'ok' ? '#3f6a44' : '#a33b2e',
          }}
        >
          {notice.text}
        </div>
      )}
      <SavingsReviewWorksheet
        plan={plan}
        lines={lines}
        optionsByItemId={optionsByItemId}
        itemsById={itemsById}
        vendorsById={vendorsById}
        comparisonByItemId={comparisonByItemId}
        workingSetGroups={workingSetGroups}
        shoppingCandidateGraph={shoppingCandidateGraph}
        canonicalItemIdByGroupId={canonicalItemIdByGroupId}
        completedVendorIds={completedVendorIds}
        draftPoByVendorId={draftPoByVendorId}
        busyVendorId={busyVendorId}
        highlightedLineIds={highlightedLineIds}
        packageAnalysisByOptionId={packageAnalysisByOptionId}
        onLineMoved={handleLineMoved}
        onAnalyzePackage={handleAnalyzePackage}
        onConfirmPackage={handleConfirmPackage}
        onEditOrderingRules={handleEditOrderingRules}
        onCompleteVendor={handleCompleteVendor}
        onCompleteVendors={handleCompleteVendors}
        finishInFlight={finishInFlight}
        orderUseByOptionId={orderUseByOptionId}
        onReturnDraft={handleReturnDraft}
        onReopenDraftAndMove={handleReopenDraftAndMove}
        onViewOrders={onViewOrders}
        onExplainScenario={handleExplainScenario}
        parContextByItemId={parContextByItemId}
      />
      {orderingRulesOption && (
        <OrderingRulesEditor
          option={orderingRulesOption}
          onClose={() => setOrderingRulesOption(null)}
          onSaved={handleOrderingRulesSaved}
        />
      )}
      {modeQuestion && (
        <VendorBuyingModeModal
          vendors={modeQuestion.vendors}
          note={modeQuestion.note}
          continueLabel={modeQuestion.continueLabel || 'Continue'}
          onAnswer={handleBuyingModeAnswer}
          onCancel={() => { setModeQuestion(null); setFinishInFlight(false); }}
        />
      )}
    </div>
  );
};

export default SavingsReviewContainer;
